import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { supabase } from '../lib/supabase'
import {
  getSessionToken,
  markSessionRegistered,
  clearSessionLocalStorage,
} from './useSessionEnforcement'

const AuthContext = createContext(null)

const SESSION_REGISTER_FAILED_ERROR = 'Session register failed. Try again.'
const PASSKEY_LOGIN_DISABLED_ERROR = 'WebAuthn login is disabled. Use email and password.'

async function isLoginWebAuthnEnabled(userId) {
  if (!userId) return true
  try {
    const { data, error } = await supabase
      .from('system_settings')
      .select('login_webauthn_enabled')
      .eq('user_id', userId)
      .single()

    if (error || typeof data?.login_webauthn_enabled !== 'boolean') return true
    return data.login_webauthn_enabled
  } catch {
    return true
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [biometricPending, setBiometricPending] = useState(false)
  // Live session ref — always holds the LATEST tokens. API call sites that
  // need a fresh access_token should read sessionRef.current rather than
  // depend on the React `session` state, which is intentionally only updated
  // on login/logout to avoid cascading re-renders on every Supabase
  // TOKEN_REFRESHED (which fires on every tab focus).
  const sessionRef = useRef(null)

  // FA-01-10 + FA-01-11: shared fail-closed cleanup. Lifted out of
  // signInWithPasswordAndBiometric so signIn / signInWithDiscoverablePasskey
  // / signInWithPasskey / completeBiometric / clearBiometricPending all
  // converge on a single definition. Always clears localStorage session
  // keys so the next user on this browser does not inherit.
  const failClosed = useCallback(async () => {
    try { await supabase.auth.signOut() } catch { /* ignore — proceeding to clear local state regardless */ }
    clearSessionLocalStorage()
    sessionRef.current = null
    setUser(null)
    setSession(null)
    setBiometricPending(false)
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user)
        setSession(session)
        sessionRef.current = session

        // FA-01-passkey-hardening (FA-01-09b): authoritative server-side
        // passkey-status check. The client must NEVER read user_passkeys
        // directly via RLS to decide whether to raise the gate — stale
        // JWTs and RLS shadows silently dropped the row count to zero,
        // releasing the biometric gate in production on 2026-05-21.
        //
        // Three-state behavior:
        //   200 + hasPasskeys=true  → raise gate (user must re-verify)
        //   200 + hasPasskeys=false → leave gate down (truly no passkeys)
        //   anything else           → fail closed: signOut + clear local
        //                              state + flag for AuthPage banner
        let statusUnknown = false
        try {
          const loginWebAuthnEnabled = await isLoginWebAuthnEnabled(session.user.id)
          if (loginWebAuthnEnabled) {
            const res = await fetch('/api/auth/passkey-status', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
            })
            if (!res.ok) {
              statusUnknown = true
            } else {
              let data
              try {
                data = await res.json()
              } catch {
                statusUnknown = true
              }
              if (!statusUnknown) {
                // Strict body contract: anything except a boolean
                // hasPasskeys is "status unknown → fail closed."
                // A 200 with `{}` or `{ hasPasskeys: 'true' }` would
                // otherwise be silently fail-open.
                if (data && data.hasPasskeys === true) {
                  setBiometricPending(true)
                } else if (data && data.hasPasskeys === false) {
                  // leave gate down — truly no passkeys
                } else {
                  statusUnknown = true
                }
              } else {
                statusUnknown = true
              }
            }
          }
        } catch {
          statusUnknown = true
        }

        if (statusUnknown) {
          // Full fail-closed cleanup. Explicitly clear local state to
          // prevent the brief Erevis-UI render between signOut() and the
          // onAuthStateChange SIGNED_OUT event firing.
          try { sessionStorage.setItem('erevis_passkey_status_unknown', '1') } catch { /* sessionStorage unavailable */ }
          await supabase.auth.signOut()
          clearSessionLocalStorage()
          sessionRef.current = null
          setUser(null)
          setSession(null)
          setBiometricPending(false)
        }
      }
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Always keep the ref current — API callers read sessionRef.current
      // for the latest access_token without triggering React re-renders.
      sessionRef.current = newSession

      // --- Diagnostic logging (remove after confirming fix) ---
      console.log('[useAuth] onAuthStateChange:', event,
        'userId:', newSession?.user?.id?.slice(0, 8) ?? 'null')

      // TOKEN_REFRESHED fires on every tab focus. It MUST be invisible to
      // the React tree — no setSession, memoized setUser. Updating state
      // here cascades through 30+ useCallback/useEffect hooks that depend
      // on [user] and causes the entire workspace to re-fetch.
      if (event === 'TOKEN_REFRESHED') {
        setUser(prev => {
          if (prev?.id === newSession?.user?.id) return prev
          return newSession?.user ?? null
        })
        return
      }

      // SIGNED_IN can also fire on tab return when Supabase exchanges an
      // expired access token via the refresh token. Same memoization rule:
      // if the user identity hasn't changed, preserve existing references
      // to prevent cascading re-renders and data re-fetches.
      if (event === 'SIGNED_IN') {
        setUser(prev => {
          if (prev?.id === newSession?.user?.id) return prev
          return newSession?.user ?? null
        })
        setSession(prev => {
          // If same user is already loaded, keep the old reference stable.
          // The ref has the fresh token for API calls — React doesn't need it.
          if (prev?.user?.id === newSession?.user?.id && prev?.user?.id != null) return prev
          return newSession ?? null
        })
        return
      }

      // All other events (SIGNED_OUT, USER_UPDATED, INITIAL_SESSION, etc.)
      // are genuine state changes — update both user and session normally.
      // Still memoize user to avoid unnecessary re-renders on USER_UPDATED
      // when the id hasn't changed.
      setUser(prev => {
        if (prev?.id === newSession?.user?.id) return prev
        return newSession?.user ?? null
      })
      setSession(newSession ?? null)
    })
    return () => subscription.unsubscribe()
  }, [])

  // FA-01-10: result-returning registerSession. Callers MUST react to
  // success === false; the prior fire-and-forget shape silently dropped
  // 500s and JSON parse failures on the floor, landing users on the
  // workspace with no row in active_sessions (single-session enforcement
  // would then kick them on the next poll).
  const registerSession = async (accessToken) => {
    const token = getSessionToken()
    const ua = navigator.userAgent
    let deviceName = 'Unknown device'
    if (/iPhone/.test(ua)) deviceName = 'iPhone'
    else if (/iPad/.test(ua)) deviceName = 'iPad'
    else if (/Macintosh/.test(ua)) deviceName = 'Mac'
    else if (/Windows/.test(ua)) deviceName = 'Windows PC'
    else if (/Android/.test(ua)) deviceName = 'Android'

    let res
    try {
      res = await fetch('/api/auth/session-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ sessionToken: token, deviceName }),
      })
    } catch {
      return { success: false, error: 'Session register failed (network)' }
    }

    if (!res.ok) {
      return { success: false, error: 'Session register failed' }
    }

    try {
      await res.json()
    } catch {
      return { success: false, error: 'Session register failed (parse)' }
    }

    markSessionRegistered()
    return { success: true }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) return { error }
    if (data?.session) {
      const reg = await registerSession(data.session.access_token)
      if (!reg.success) {
        await failClosed()
        return { error: SESSION_REGISTER_FAILED_ERROR }
      }
    }
    return { error: null }
  }

  const signUp = async (email, password) => {
    const { error } = await supabase.auth.signUp({ email, password })
    return { error }
  }

  const signOut = async () => {
    // FA-01-11: clear localStorage session keys BEFORE Supabase signOut
    // so the next user on this browser does not inherit the previous
    // user's session_token.
    clearSessionLocalStorage()
    await supabase.auth.signOut()
  }

  const signInWithPasswordAndBiometric = async (email, password) => {
    // Set gate BEFORE signing in — prevents dashboard flash
    setBiometricPending(true)

    // Step 1: Verify password
    const { data: pwData, error: pwError } = await supabase.auth.signInWithPassword({ email, password })
    if (pwError) {
      setBiometricPending(false)
      return { error: pwError }
    }

    const loginWebAuthnEnabled = await isLoginWebAuthnEnabled(pwData?.session?.user?.id)
    if (!loginWebAuthnEnabled) {
      setBiometricPending(false)
      if (pwData?.session) {
        const reg = await registerSession(pwData.session.access_token)
        if (!reg.success) {
          await failClosed()
          return { error: SESSION_REGISTER_FAILED_ERROR }
        }
      }
      return {}
    }

    // Step 2: Check if user has passkeys via passkey-auth-options. ANY
    // non-200 / parse-fail / thrown error must sign the user out — never
    // fall through to password-only Erevis access (FA-01-09c was live in
    // production on 2026-05-21 because the catch returned `{}`).
    try {
      const optRes = await fetch('/api/auth/passkey-auth-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!optRes.ok) {
        // Generic error string only — body may be HTML / malformed JSON
        // / empty. Don't surface err.message either (may leak internals).
        await failClosed()
        return { error: 'Passkey status unavailable. Try again.' }
      }

      let optData
      try {
        optData = await optRes.json()
      } catch {
        await failClosed()
        return { error: 'Passkey status unavailable. Try again.' }
      }

      // Strict body contract: hasPasskeys must be a boolean. A 200 with
      // `{}` or a missing field would otherwise be silently treated as
      // "no passkeys → release into password-only" (the exact FA-01-09c
      // shape that shipped to prod 2026-05-21).
      if (typeof optData?.hasPasskeys !== 'boolean') {
        await failClosed()
        return { error: 'Passkey status unavailable. Try again.' }
      }

      // No passkeys registered — password-only login, release gate
      if (!optData.hasPasskeys) {
        setBiometricPending(false)
        if (pwData?.session) {
          const reg = await registerSession(pwData.session.access_token)
          if (!reg.success) {
            await failClosed()
            return { error: SESSION_REGISTER_FAILED_ERROR }
          }
        }
        return {}
      }

      // Passkeys exist — return to caller so AuthPage can show the biometric screen
      // Gate stays up (biometricPending = true) until completeBiometric() or cancelBiometric()
      return { biometricRequired: true, authOptions: optData }
    } catch {
      await failClosed()
      return { error: 'Passkey status unavailable. Try again.' }
    }
  }

  const completeBiometric = async () => {
    setBiometricPending(false)
    const accessToken = sessionRef.current?.access_token || session?.access_token
    if (accessToken) {
      const reg = await registerSession(accessToken)
      if (!reg.success) {
        await failClosed()
        return { success: false, error: SESSION_REGISTER_FAILED_ERROR }
      }
    }
    return { success: true }
  }

  const cancelBiometric = async () => {
    // FA-01-11: route through the same cleanup as signOut/failClosed
    // so the localStorage session keys are removed in lockstep with
    // Supabase signOut.
    clearSessionLocalStorage()
    await supabase.auth.signOut()
    setBiometricPending(false)
  }

  const signInWithDiscoverablePasskey = async () => {
    try {
      const optRes = await fetch('/api/auth/passkey-auth-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const optData = await optRes.json()
      if (!optData.hasPasskeys) {
        return { error: 'No passkeys registered. Use password to sign in.' }
      }

      const { startAuthentication } = await import('@simplewebauthn/browser')
      let authResponse
      try {
        authResponse = await startAuthentication({ optionsJSON: optData.options })
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          return { error: 'Passkey authentication was cancelled.' }
        }
        throw err
      }

      // FA-01-passkey-hardening: post the challengeId returned by the
      // per-id discoverable flow. Atomically coupled with the server-side
      // per-id rewrite in passkey-auth-options.js — without this, the
      // server returns "Missing challengeId" (400) and discoverable
      // login breaks entirely.
      const verifyRes = await fetch('/api/auth/passkey-auth-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: authResponse, discoverable: true, challengeId: optData.challengeId }),
      })
      const verifyData = await verifyRes.json()
      if (!verifyRes.ok || !verifyData.success) {
        return { error: verifyData.error || 'Passkey verification failed' }
      }

      const { data: otpData, error: otpError } = await supabase.auth.verifyOtp({
        token_hash: verifyData.token_hash,
        type: 'magiclink',
      })
      if (otpError) {
        return { error: otpError.message }
      }

      const loginWebAuthnEnabled = await isLoginWebAuthnEnabled(otpData?.session?.user?.id)
      if (!loginWebAuthnEnabled) {
        await failClosed()
        return { error: PASSKEY_LOGIN_DISABLED_ERROR }
      }

      if (otpData?.session) {
        const reg = await registerSession(otpData.session.access_token)
        if (!reg.success) {
          await failClosed()
          return { error: SESSION_REGISTER_FAILED_ERROR }
        }
      }
      return { success: true }
    } catch (err) {
      console.error('Discoverable passkey error:', err)
      return { error: err.message || 'Passkey authentication failed' }
    }
  }

  const clearBiometricPending = async () => {
    setBiometricPending(false)
    const accessToken = sessionRef.current?.access_token || session?.access_token
    if (accessToken) {
      const reg = await registerSession(accessToken)
      if (!reg.success) {
        await failClosed()
        return { success: false, error: SESSION_REGISTER_FAILED_ERROR }
      }
    }
    return { success: true }
  }

  const signInWithPasskey = async (email) => {
    try {
      // 1. Get authentication options from backend
      const optRes = await fetch('/api/auth/passkey-auth-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const optData = await optRes.json()
      if (!optRes.ok) return { error: optData.error || 'Failed to get passkey options' }
      if (!optData.hasPasskeys) return { error: 'No passkeys registered for this account', hasPasskeys: false }

      // 2. Trigger native Touch ID / Face ID / Windows Hello prompt
      let assertion
      try {
        assertion = await startAuthentication({ optionsJSON: optData.options })
      } catch (err) {
        if (err.name === 'NotAllowedError') return { error: 'Touch ID was cancelled or not allowed' }
        return { error: err.message || 'Touch ID failed' }
      }

      // 3. Verify with backend
      const verifyRes = await fetch('/api/auth/passkey-auth-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, response: assertion }),
      })
      const verifyData = await verifyRes.json()
      if (!verifyRes.ok) return { error: verifyData.error || 'Passkey verification failed' }

      // 4. Exchange token_hash for a Supabase session
      const { data: otpData, error: otpError } = await supabase.auth.verifyOtp({
        token_hash: verifyData.token_hash,
        type: 'magiclink',
      })
      if (otpError) return { error: otpError.message }

      const loginWebAuthnEnabled = await isLoginWebAuthnEnabled(otpData?.session?.user?.id)
      if (!loginWebAuthnEnabled) {
        await failClosed()
        return { error: PASSKEY_LOGIN_DISABLED_ERROR }
      }

      // 5. Register session + onAuthStateChange fires automatically
      if (otpData?.session) {
        const reg = await registerSession(otpData.session.access_token)
        if (!reg.success) {
          await failClosed()
          return { error: SESSION_REGISTER_FAILED_ERROR }
        }
      }
      return {}
    } catch (err) {
      return { error: err.message || 'Passkey sign-in failed' }
    }
  }

  return (
    <AuthContext.Provider value={{
      user, session, sessionRef, loading, biometricPending,
      signIn, signUp, signOut,
      signInWithPasskey, signInWithDiscoverablePasskey, signInWithPasswordAndBiometric,
      completeBiometric, cancelBiometric, clearBiometricPending,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
